import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle2, Clock, ArrowDownCircle, Coins, Gem } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { apiRequest } from '@/lib/queryClient';
import { format, formatDistanceToNow } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';

interface DailyRewardTransaction {
  id: number;
  amount: number;
  currencyType: 'regular' | 'premium';
  description: string;
  createdAt: string;
  day: number;
}

export function DailyRewardStreak() {
  const [currentStreak, setCurrentStreak] = useState(1);
  const [lastClaimDate, setLastClaimDate] = useState<string | null>(null);
  const [rewardHistory, setRewardHistory] = useState<DailyRewardTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Load streak data from localStorage
  const loadStreakData = () => {
    const savedStreak = localStorage.getItem("currentDailyStreak");
    if (savedStreak) {
      setCurrentStreak(parseInt(savedStreak, 10));
    }
    
    const lastClaimedDate = localStorage.getItem("lastDailyRewardClaim");
    if (lastClaimedDate) {
      setLastClaimDate(lastClaimedDate);
    }
  };

  // Fetch reward history
  const fetchRewardHistory = async () => {
    try {
      setIsLoading(true);
      const response = await apiRequest('GET', '/api/currency/transactions?type=daily_reward&limit=30');
      const transactions = await response.json();
      
      // Extract day number from description (format: "Daily Reward (Day X): ...")
      const processedTransactions = transactions.map((tx: any) => {
        const dayMatch = tx.description.match(/Daily Reward \(Day (\d+)\):/);
        return {
          ...tx,
          day: dayMatch ? parseInt(dayMatch[1], 10) : 0
        };
      });
      
      setRewardHistory(processedTransactions);
    } catch (error) {
      console.error('Error fetching reward history:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    // Initial load of streak data
    loadStreakData();
    fetchRewardHistory();
    
    // Create custom event listener to update streak when rewards are claimed
    const handleRewardClaimed = () => {
      loadStreakData();
      fetchRewardHistory();
    };
    
    // Add event listener
    window.addEventListener("dailyRewardClaimed", handleRewardClaimed);
    
    // Clean up event listener
    return () => {
      window.removeEventListener("dailyRewardClaimed", handleRewardClaimed);
    };
  }, []);

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return "Never claimed";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Determine if claimed today
  const isClaimedToday = () => {
    if (!lastClaimDate) return false;
    return lastClaimDate === new Date().toDateString();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 5 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.2
      }
    }
  };

  // Generate array of 30 days organized in rows of 7 (like a mini calendar)
  const totalDays = 30;
  const daysPerRow = 7;
  const rows = Math.ceil(totalDays / daysPerRow);
  
  return (
    <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-amber-400" />
          <span className="text-sm font-medium text-white">Streak Status</span>
        </div>
        {isClaimedToday() && (
          <Badge className="bg-green-600 text-white">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Claimed Today
          </Badge>
        )}
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between text-xs text-gray-300">
          <span>Current streak:</span>
          <span className="font-medium text-amber-400">{currentStreak} day{currentStreak !== 1 ? 's' : ''}</span>
        </div>
        
        {lastClaimDate && (
          <div className="flex justify-between text-xs text-gray-300">
            <span>Last claimed:</span>
            <span>{formatDate(lastClaimDate)}</span>
          </div>
        )}
        
        <div className="pt-1 pb-2">
          {/* Calendar-style dots display */}
          <div className="flex flex-col gap-2">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <div key={rowIndex} className="flex justify-between gap-2">
                {Array.from({ length: daysPerRow }).map((_, colIndex) => {
                  const dayNumber = rowIndex * daysPerRow + colIndex + 1;
                  // Don't render additional empty spaces, only render totalDays dots
                  if (dayNumber > totalDays) return null;
                  
                  // Bug fix: dayNumber starts at 1, so we need <= comparison to show all days
                  const isActive = dayNumber <= currentStreak;
                  const isNext = dayNumber === currentStreak + 1;
                  
                  return (
                    <div 
                      key={colIndex} 
                      className={`h-3 w-3 rounded-full flex-1 ${
                        isActive 
                          ? "bg-amber-500" 
                          : isNext 
                            ? "bg-amber-800/60" 
                            : "bg-gray-700"
                      }`}
                      title={`Day ${dayNumber}`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-xs text-gray-500">Day 1</span>
            <span className="text-xs text-gray-500">Day {totalDays}</span>
          </div>
        </div>
        
        <div className="text-xs text-gray-400 italic mb-3">
          {isClaimedToday() 
            ? "Continue your streak tomorrow for more rewards!" 
            : "Claim today's reward to continue your streak!"}
        </div>
        
        {/* Reward History Section */}
        <div className="border-t border-gray-700 pt-3 mt-3">
          <div className="flex items-center gap-1.5 mb-3">
            <Clock className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-sm font-medium text-gray-200">Recent Rewards</span>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-3">
              <div className="h-5 w-5 border-t-2 border-b-2 border-amber-400 rounded-full animate-spin" />
            </div>
          ) : rewardHistory.length === 0 ? (
            <div className="text-center py-2 text-xs text-gray-500">
              No rewards claimed yet
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar"
            >
              {rewardHistory.map(transaction => (
                <motion.div key={transaction.id} variants={itemVariants}>
                  <Card className="bg-gray-900/50 border-gray-800">
                    <CardContent className="p-2 text-xs">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="h-5 px-1.5 bg-amber-950/40 text-amber-500 border-amber-900/50">
                            Day {transaction.day}
                          </Badge>
                          <span className="text-gray-400">
                            {formatDistanceToNow(new Date(transaction.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ArrowDownCircle className="h-3 w-3 text-green-400" />
                          <span className="font-medium text-green-400">{transaction.amount}</span>
                          {transaction.currencyType === 'premium' ? (
                            <Gem className="h-3 w-3 text-purple-400" />
                          ) : (
                            <Coins className="h-3 w-3 text-amber-400" />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}